import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Image, Send, ThumbsUp, MessageCircle, Share2, MoreHorizontal, Camera, Paperclip, X } from 'lucide-react';
import { Post } from '../types';
import { getMockPosts } from '../data/mockData';
import PhotoPost, { PhotoPostData } from '../components/media/PhotoPost';
import PhotoUploadWidget from '../components/media/PhotoUploadWidget';

const Feed = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [newPostContent, setNewPostContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showPhotoPost, setShowPhotoPost] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [showQuickUpload, setShowQuickUpload] = useState(false);

  useEffect(() => {
    document.title = 'Feed - FemSportNet';
    
    // Simulate fetching posts
    const fetchPosts = async () => {
      try {
        setIsLoading(true);
        // In a real app, this would be an API call
        const data = await getMockPosts();
        setPosts(data);
      } catch (error) {
        console.error('Error fetching posts:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const handleCreatePost = () => {
    if (!newPostContent.trim() || !user) return;

    const newPost: Post = {
      id: Math.random().toString(36).substring(2, 15),
      userId: user.id,
      user: user,
      content: newPostContent,
      likes: 0,
      comments: 0,
      createdAt: new Date().toISOString(),
      isLiked: false
    };

    setPosts([newPost, ...posts]);
    setNewPostContent('');
  };

  const handlePhotoPost = async (postData: PhotoPostData) => {
    setUploadLoading(true);
    try {
      // Simulate upload process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Create new post with photos
      const newPost: Post = {
        id: Math.random().toString(36).substring(2, 15),
        userId: user?.id || '',
        user: user,
        content: postData.content,
        images: postData.photos.map(file => URL.createObjectURL(file)),
        likes: 0,
        comments: 0,
        createdAt: new Date().toISOString(),
        isLiked: false
      };

      setPosts([newPost, ...posts]);
      setShowPhotoPost(false);
      
    } catch (error) {
      console.error('Error uploading photos:', error);
      alert('Error al subir las fotos. Intenta nuevamente.');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleQuickPhotoUpload = (urls: string[]) => {
    // Create a simple post with uploaded photos
    const newPost: Post = {
      id: Math.random().toString(36).substring(2, 15),
      userId: user?.id || '',
      user: user,
      content: '📸 Nuevas fotos compartidas',
      images: urls,
      likes: 0,
      comments: 0,
      createdAt: new Date().toISOString(),
      isLiked: false
    };

    setPosts([newPost, ...posts]);
    setShowQuickUpload(false);
  };

  const handleLikePost = (postId: string) => {
    setPosts(posts.map(post => {
      if (post.id === postId) {
        const isLiked = !post.isLiked;
        return {
          ...post,
          isLiked,
          likes: isLiked ? post.likes + 1 : post.likes - 1
        };
      }
      return post;
    }));
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-200">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Create Post */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-8 transition-colors duration-200">
            <div className="p-4">
              <div className="flex items-start space-x-4">
                <Link to={`/profile/${user?.id}`} className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full overflow-hidden">
                    <img 
                      src={user?.avatar} 
                      alt={user?.name} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </Link>
                <div className="flex-grow">
                  <textarea
                    className="w-full border border-gray-200 dark:border-gray-700 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 text-gray-900 dark:text-white resize-none transition-colors duration-200"
                    placeholder="What's on your mind?"
                    rows={3}
                    value={newPostContent}
                    onChange={(e) => setNewPostContent(e.target.value)}
                  ></textarea>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between">
              <div className="flex space-x-4">
                <button className="flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors duration-200">
                  <Paperclip size={20} className="mr-2" />
                  <span className="text-sm">Archivo</span>
                </button>
                <button 
                  onClick={() => setShowQuickUpload(true)}
                  className="flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors duration-200"
                >
                  <Camera size={20} className="mr-2" />
                  <span className="text-sm">Fotos Rápidas</span>
                </button>
                <button 
                  onClick={() => setShowPhotoPost(true)}
                  className="flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors duration-200"
                >
                  <Image size={20} className="mr-2" />
                  <span className="text-sm">Post con Fotos</span>
                </button>
              </div>
              <button
                onClick={handleCreatePost}
                disabled={!newPostContent.trim()}
                className={`flex items-center px-4 py-2 rounded-md text-white ${
                  newPostContent.trim() 
                    ? 'bg-green-500 hover:bg-green-600' 
                    : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
                } transition-colors duration-200`}
              >
                <Send size={18} className="mr-2" />
                <span>Post</span>
              </button>
            </div>
          </div>

          {/* Quick Photo Upload */}
          {showQuickUpload && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-8 p-6 transition-colors duration-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Subida Rápida de Fotos</h3>
                <button
                  onClick={() => setShowQuickUpload(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X size={20} />
                </button>
              </div>
              <PhotoUploadWidget
                onUploadComplete={handleQuickPhotoUpload}
                onUploadError={(error) => {
                  console.error('Upload error:', error);
                  alert(error);
                }}
                maxPhotos={3}
                showPreview={true}
              />
            </div>
          )}

          {/* Photo Post Modal */}
          {showPhotoPost && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
              <div className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <PhotoPost
                  onSubmit={handlePhotoPost}
                  onCancel={() => setShowPhotoPost(false)}
                  isLoading={uploadLoading}
                />
              </div>
            </div>
          )}

          {/* Posts Feed */}
          {isLoading ? (
            <div className="flex justify-center my-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {posts.map((post) => (
                <div key={post.id} className="bg-white dark:bg-gray-800 rounded-lg shadow transition-colors duration-200">
                  {/* Post Header */}
                  <div className="p-4 flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <Link to={`/profile/${post.user?.id}`} className="flex-shrink-0">
                        <div className="w-10 h-10 rounded-full overflow-hidden">
                          <img 
                            src={post.user?.avatar} 
                            alt={post.user?.name} 
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </Link>
                      <div>
                        <Link to={`/profile/${post.user?.id}`} className="font-medium text-gray-900 dark:text-white hover:underline">
                          {post.user?.name}
                        </Link>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(post.createdAt).toLocaleDateString()} · {post.user?.sport}
                        </p>
                      </div>
                    </div>
                    <button className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors duration-200">
                      <MoreHorizontal size={20} />
                    </button>
                  </div>
                  
                  {/* Post Content */}
                  <div className="px-4 pb-2">
                    <p className="text-gray-800 dark:text-gray-200 whitespace-pre-line">
                      {post.content}
                    </p>
                  </div>
                  
                  {/* Post Images */}
                  {post.images && post.images.length > 0 && (
                    <div className="mt-2">
                      <img 
                        src={post.images[0]} 
                        alt="Post attachment" 
                        className="w-full h-auto object-cover"
                      />
                    </div>
                  )}
                  
                  {/* Post Video */}
                  {post.video && (
                    <div className="mt-2">
                      <video 
                        src={post.video} 
                        className="w-full h-auto" 
                        controls 
                      />
                    </div>
                  )}
                  
                  {/* Post Stats */}
                  <div className="px-4 py-2 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 mt-2">
                    <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                      <span>{post.likes} likes</span>
                      <span>·</span>
                      <span>{post.comments} comments</span>
                    </div>
                  </div>
                  
                  {/* Post Actions */}
                  <div className="px-4 py-2 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
                    <button 
                      onClick={() => handleLikePost(post.id)}
                      className={`flex items-center space-x-1 px-3 py-1 rounded-md transition-colors duration-200 ${
                        post.isLiked 
                          ? 'text-green-500 dark:text-green-400' 
                          : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <ThumbsUp size={18} />
                      <span>Like</span>
                    </button>
                    <button className="flex items-center space-x-1 px-3 py-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors duration-200">
                      <MessageCircle size={18} />
                      <span>Comment</span>
                    </button>
                    <button className="flex items-center space-x-1 px-3 py-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors duration-200">
                      <Share2 size={18} />
                      <span>Share</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Feed;